import { useCallback, useState } from "react";
import { isGroupTaskRow, resolveTaskId } from "./SiTableCell";
import { normalizeSi } from "./siUtils";
import { toggleTaskSi } from "./taskSiApi";

/**
 * Toggle task SI via PATCH /si/:taskId.
 * Applies an optimistic local update so the switch reflects immediately.
 */
export function useSiToggle({ accessToken, onUpdated, onError }) {
  const [siUpdatingIds, setSiUpdatingIds] = useState(() => new Set());

  const handleSiChange = useCallback(
    async (row) => {
      const taskId = resolveTaskId(row);
      if (!taskId || isGroupTaskRow(row)) return;

      const idKey = String(taskId);
      const previousSi = normalizeSi(row?.si);
      const optimisticSi = previousSi === 1 ? 0 : 1;

      onUpdated?.(taskId, optimisticSi);

      setSiUpdatingIds((prev) => new Set(prev).add(idKey));
      try {
        const serverSi = await toggleTaskSi({ taskId, accessToken });
        if (serverSi != null && serverSi !== optimisticSi) {
          onUpdated?.(taskId, serverSi);
        }
      } catch (err) {
        onUpdated?.(taskId, previousSi);
        onError?.(err);
      } finally {
        setSiUpdatingIds((prev) => {
          const next = new Set(prev);
          next.delete(idKey);
          return next;
        });
      }
    },
    [accessToken, onError, onUpdated],
  );

  return { handleSiChange, siUpdatingIds };
}
